import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Pagination,
} from '@mui/material';
import { Add, CheckCircle, CloudUpload, PhotoCamera } from '@mui/icons-material';
import api from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';

interface TechnicianUser {
  id: number;
  name: string;
  name_ar?: string;
}

interface Technician {
  id: number;
  user?: TechnicianUser | null;
}

interface OutOfSiteTask {
  id: number;
  technician_id: number | null;
  objective: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  start_time: string;
  expected_end_time?: string | null;
  end_time?: string | null;
  notes?: string | null;
  attachments?: string[] | null;
  technician?: Technician | null;
  creator?: TechnicianUser | null;
}

// The app's business timezone. Times entered in the form are treated as Kuwait time.
const KUWAIT_TZ = 'Asia/Kuwait';
const KUWAIT_UTC_OFFSET = '+03:00'; // Kuwait is UTC+3 year-round (no DST)

// Returns the current KUWAIT wall-clock time as "yyyy-MM-ddTHH:mm" for <input type="datetime-local">.
const kuwaitDateTimeInput = (date: Date = new Date()): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: KUWAIT_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(date).reduce<Record<string, string>>((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  const hour = parts.hour === '24' ? '00' : parts.hour; // some engines emit '24' for midnight
  return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}`;
};

// Converts a "yyyy-MM-ddTHH:mm" value (interpreted as Kuwait time) to a UTC ISO string.
const kuwaitInputToUtcIso = (value: string): string =>
  new Date(`${value}:00${KUWAIT_UTC_OFFSET}`).toISOString();

const OutOfSiteTasks: React.FC = () => {
  const { t, language } = useLanguage();
  const { notifySuccess, notifyError } = useNotification();
  const { user, technicianId } = useAuth();

  const [tasks, setTasks] = useState<OutOfSiteTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [creating, setCreating] = useState(false);
  const [completingId, setCompletingId] = useState<number | null>(null);

  const [filters, setFilters] = useState({
    status: 'all',
    technician_id: '',
    date_from: '',
    date_to: '',
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    technician_id: '',
    objective: '',
    start_time: '',
    expected_end_time: '',
    notes: '',
  });

  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreview, setImagePreview] = useState<string[]>([]);

  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);

  // Camera capture state (full-screen camera like Purchase Orders / Expenses)
  const [cameraOpen, setCameraOpen] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Image preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);

  const statusChipColor = (status: OutOfSiteTask['status']) => {
    switch (status) {
      case 'in_progress':
        return 'primary';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return 'N/A';
    const d = new Date(value);
    return d.toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getImageUrl = (path: string) => {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    const base = (api.defaults.baseURL as string || '').replace(/\/api\/?$/, '');
    return `${base}/storage/${path}`;
  };

  const fetchTasks = async (page: number = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('per_page', '10');
      if (filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.technician_id) {
        params.append('technician_id', filters.technician_id);
      }
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      const res = await api.get(`/out-of-site-tasks?${params.toString()}`);
      const data = res.data;
      const rows = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);

      setTasks(rows);
      if (data?.last_page) {
        setTotalPages(data.last_page);
        setTotalCount(data.total || rows.length);
      } else {
        setTotalPages(1);
        setTotalCount(rows.length);
      }
      setCurrentPage(page);
    } catch (e: any) {
      console.error('Failed to load out-of-site tasks', e);
      notifyError(e?.response?.data?.error || t('outOfSite.messages.load.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load technicians list only when needed (admin/manager who can choose technician)
  useEffect(() => {
    const shouldLoadTechnicians = !technicianId; // admins/managers usually have no technicianId
    if (!shouldLoadTechnicians) return;

    const loadTechnicians = async () => {
      try {
        setLoadingTechnicians(true);
        const response = await api.get('/technicians');
        const rows = Array.isArray(response.data) ? response.data : [];
        setTechnicians(rows);
      } catch (e) {
        console.error('Failed to load technicians for Out-of-Site Tasks', e);
        setTechnicians([]);
      } finally {
        setLoadingTechnicians(false);
      }
    };

    loadTechnicians();
  }, [technicianId]);

  const handleOpenCreate = () => {
    const nowIso = kuwaitDateTimeInput(); // current Kuwait time as yyyy-MM-ddTHH:mm
    setForm({
      technician_id: technicianId ? String(technicianId) : '',
      objective: '',
      start_time: nowIso,
      expected_end_time: '',
      notes: '',
    });
    setSelectedImages([]);
    setImagePreview([]);
    setCreateOpen(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setSelectedImages(prev => [...prev, ...files]);
    const previews = files.map(file => URL.createObjectURL(file));
    setImagePreview(prev => [...prev, ...previews]);
  };

  // Camera capture helpers (modeled after Expenses camera)
  const openCamera = async () => {
    setCameraOpen(true);
    try {
      let mediaStream: MediaStream | null = null;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });
      } catch {
        // Fallback to any available camera
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
      }

      if (mediaStream) {
        setStream(mediaStream);
      } else {
        setCameraOpen(false);
        notifyError('Unable to access camera.');
      }
    } catch (error: any) {
      console.error('Error accessing camera:', error);
      setCameraOpen(false);
      const msg =
        error?.name === 'NotAllowedError'
          ? 'Camera permission denied.'
          : error?.name === 'NotFoundError'
          ? 'No camera found on this device.'
          : 'Unable to access camera. Please check permissions.';
      notifyError(msg);
    }
  };

  const closeCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      setStream(null);
    }
    setCameraOpen(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    canvas.toBlob((blob: Blob | null) => {
      if (!blob) return;
      const file = new File([blob], `out-of-site-task-${Date.now()}.jpg`, {
        type: 'image/jpeg',
      });
      setSelectedImages(prev => [...prev, file]);
      setImagePreview(prev => [...prev, URL.createObjectURL(file)]);
      closeCamera();
    }, 'image/jpeg', 0.9);
  };

  // Attach stream to video element
  useEffect(() => {
    if (stream && videoRef.current) {
      const video = videoRef.current;
      video.srcObject = stream;
      video
        .play()
        .catch((error: any) => console.error('Error playing video:', error));
    } else if (videoRef.current && !stream) {
      videoRef.current.srcObject = null;
    }
  }, [stream, cameraOpen]);

  const handleCreate = async () => {
    try {
      setCreating(true);
      const formData = new FormData();
      if (form.technician_id) formData.append('technician_id', form.technician_id);
      formData.append('objective', form.objective);
      if (form.start_time) formData.append('start_time', kuwaitInputToUtcIso(form.start_time));
      if (form.expected_end_time) formData.append('expected_end_time', kuwaitInputToUtcIso(form.expected_end_time));
      if (form.notes) formData.append('notes', form.notes);
      selectedImages.forEach((file, index) => {
        formData.append(`images[${index}]`, file);
      });

      const res = await api.post('/out-of-site-tasks', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      notifySuccess(t('outOfSite.messages.create.success'));
      setCreateOpen(false);
      setTasks(prev => [res.data, ...prev]);
    } catch (e: any) {
      console.error('Failed to create out-of-site task', e);
      if (e?.response?.data?.errors) {
        notifyError(t('outOfSite.messages.create.validationError'));
      } else {
        notifyError(e?.response?.data?.error || t('outOfSite.messages.create.error'));
      }
    } finally {
      setCreating(false);
    }
  };

  const handleComplete = async (task: OutOfSiteTask) => {
    try {
      setCompletingId(task.id);
      const payload = {
        end_time: new Date().toISOString(),
      };
      const res = await api.post(`/out-of-site-tasks/${task.id}/complete`, payload);
      notifySuccess(t('outOfSite.messages.complete.success'));
      setTasks(prev => prev.map(t => (t.id === task.id ? res.data : t)));
    } catch (e: any) {
      console.error('Failed to complete out-of-site task', e);
      notifyError(e?.response?.data?.error || t('outOfSite.messages.complete.error'));
    } finally {
      setCompletingId(null);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {t('sidebar.outOfSiteTasks')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('outOfSite.header.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreate}
        >
          {t('outOfSite.actions.new')}
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            {t('outOfSite.filters.title')}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, minmax(0, 1fr))' },
              gap: 2,
              alignItems: 'center',
            }}
          >
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>{t('outOfSite.filters.status')}</InputLabel>
              <Select
                label={t('outOfSite.filters.status')}
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="all">{t('outOfSite.filters.status.all')}</MenuItem>
                <MenuItem value="in_progress">{t('outOfSite.filters.status.inProgress')}</MenuItem>
                <MenuItem value="completed">{t('outOfSite.filters.status.completed')}</MenuItem>
                <MenuItem value="cancelled">{t('outOfSite.filters.status.cancelled')}</MenuItem>
              </Select>
            </FormControl>

            {/* Technician filter only for admin/manager (no technicianId bound) */}
            {!technicianId && (
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>{t('outOfSite.filters.technician')}</InputLabel>
                <Select
                  label={t('outOfSite.filters.technician')}
                  value={filters.technician_id}
                  onChange={(e) =>
                    setFilters(prev => ({ ...prev, technician_id: e.target.value as string }))
                  }
                  disabled={loadingTechnicians}
                >
                  <MenuItem value="">
                    <em>{t('outOfSite.filters.technician.all')}</em>
                  </MenuItem>
                  {technicians.map((tech) => (
                    <MenuItem key={tech.id} value={String(tech.id)}>
                      {tech.user
                        ? (language === 'ar' && tech.user.name_ar
                          ? tech.user.name_ar
                          : tech.user.name)
                        : `#${tech.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label={t('outOfSite.filters.dateFrom')}
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={t('outOfSite.filters.dateTo')}
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
            <Button
              variant="outlined"
              onClick={() => {
                setFilters({ status: 'all', technician_id: '', date_from: '', date_to: '' });
                fetchTasks(1);
              }}
            >
              {t('outOfSite.filters.clear')}
            </Button>
            <Button variant="contained" onClick={() => fetchTasks(1)} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : t('outOfSite.filters.apply')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : tasks.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('outOfSite.list.empty')}
            </Typography>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>{t('outOfSite.table.technician')}</strong></TableCell>
                      <TableCell><strong>{t('outOfSite.table.objective')}</strong></TableCell>
                      <TableCell><strong>{t('outOfSite.table.startTime')}</strong></TableCell>
                      <TableCell><strong>{t('outOfSite.table.expectedEnd')}</strong></TableCell>
                      <TableCell><strong>{t('outOfSite.table.endTime')}</strong></TableCell>
                      <TableCell><strong>{t('outOfSite.table.photos')}</strong></TableCell>
                      <TableCell><strong>{t('outOfSite.table.status')}</strong></TableCell>
                      <TableCell align="right"><strong>{t('outOfSite.table.actions')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tasks.map(task => (
                      <TableRow key={task.id} hover>
                        <TableCell>
                          {task.technician?.user
                            ? (language === 'ar' && task.technician.user.name_ar
                              ? task.technician.user.name_ar
                              : task.technician.user.name)
                            : t('common.notAvailable')}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            noWrap
                            title={task.objective}
                            sx={{ maxWidth: 260 }}
                          >
                            {task.objective}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatDateTime(task.start_time)}</TableCell>
                        <TableCell>{formatDateTime(task.expected_end_time)}</TableCell>
                        <TableCell>{formatDateTime(task.end_time)}</TableCell>
                        <TableCell>
                          {task.attachments && task.attachments.length > 0 ? (
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              {task.attachments.slice(0, 3).map((img, idx) => (
                                <Box
                                  key={`${task.id}-img-${idx}`}
                                  component="img"
                                  src={getImageUrl(img)}
                                  alt={`attachment-${idx}`}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    objectFit: 'cover',
                                    borderRadius: 0.5,
                                    border: '1px solid #ddd',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => {
                                    setPreviewSrc(getImageUrl(img));
                                    setPreviewOpen(true);
                                  }}
                                />
                              ))}
                              {task.attachments.length > 3 && (
                                <Typography variant="caption" sx={{ ml: 0.5 }}>
                                  +{task.attachments.length - 3}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {t('outOfSite.table.noPhotos')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t(`outOfSite.status.${task.status}`)}
                            color={statusChipColor(task.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {task.status === 'in_progress' && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CheckCircle />}
                              onClick={() => handleComplete(task)}
                              disabled={completingId === task.id}
                            >
                              {completingId === task.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                t('outOfSite.actions.markCompleted')
                              )}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                <Typography variant="body2" color="text.secondary">
                  {t('outOfSite.list.total').replace('{{count}}', String(totalCount))}
                </Typography>
                {totalPages > 1 && (
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, page) => fetchTasks(page)}
                    color="primary"
                  />
                )}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('outOfSite.dialog.create.title')}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {/* Technician selection: if logged in user is a technician, show their name; otherwise dropdown */}
              {technicianId ? (
              <TextField
                label={t('outOfSite.dialog.fields.technician')}
                value={
                  user
                    ? (language === 'ar' && (user as any).name_ar ? (user as any).name_ar : user.name)
                    : ''
                }
                fullWidth
                disabled
              />
            ) : (
              <FormControl fullWidth>
                <InputLabel>{t('outOfSite.dialog.fields.technician')}</InputLabel>
                <Select
                  label={t('outOfSite.dialog.fields.technician')}
                  value={form.technician_id}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, technician_id: e.target.value as string }))
                  }
                  disabled={loadingTechnicians}
                >
                  <MenuItem value="">
                    <em>{t('outOfSite.dialog.fields.technician.select')}</em>
                  </MenuItem>
                  {technicians.map((tech) => (
                    <MenuItem key={tech.id} value={String(tech.id)}>
                      {tech.user
                        ? (language === 'ar' && tech.user.name_ar
                          ? tech.user.name_ar
                          : tech.user.name)
                        : `#${tech.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              label={t('outOfSite.dialog.fields.objective')}
              value={form.objective}
              onChange={(e) => setForm(prev => ({ ...prev, objective: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label={t('outOfSite.dialog.fields.startTime')}
              type="datetime-local"
              value={form.start_time}
              onChange={(e) => setForm(prev => ({ ...prev, start_time: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: kuwaitDateTimeInput() }}
              fullWidth
            />
            <TextField
              label={t('outOfSite.dialog.fields.expectedEndTime')}
              type="datetime-local"
              value={form.expected_end_time}
              onChange={(e) => setForm(prev => ({ ...prev, expected_end_time: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: form.start_time || kuwaitDateTimeInput() }}
              fullWidth
            />
            <TextField
              label={t('outOfSite.dialog.fields.notes')}
              value={form.notes}
              onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('outOfSite.dialog.images.title')}
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  startIcon={<CloudUpload />}
                >
                  {t('outOfSite.dialog.images.choose')}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={handleImageChange}
                  />
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PhotoCamera />}
                  onClick={openCamera}
                >
                  {t('outOfSite.dialog.images.camera')}
                </Button>
              </Box>
              {imagePreview.length > 0 && (
                <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                  {imagePreview.map((src, idx) => (
                    <Box
                      key={idx}
                      component="img"
                      src={src}
                      alt={`attachment-${idx}`}
                      sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={
              creating ||
              !form.objective.trim() ||
              (!technicianId && !form.technician_id)
            }
            >
            {creating ? <CircularProgress size={20} /> : t('outOfSite.dialog.create.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image preview dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        >
        <DialogTitle>{t('outOfSite.preview.title')}</DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#000',
          }}
        >
          {previewSrc && (
            <Box
              component="img"
              src={previewSrc}
              alt="Out-of-Site Task attachment"
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Camera Dialog (same UX as purchase order / expenses) */}
      <Dialog
        open={cameraOpen}
        onClose={closeCamera}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: 'black',
            maxWidth: '90vw',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">{t('outOfSite.camera.title')}</Typography>
          <Button onClick={closeCamera} sx={{ color: 'white' }}>
            {t('common.close')}
          </Button>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {stream ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                style={{
                  width: '100%',
                  maxWidth: '800px',
                  maxHeight: '600px',
                  borderRadius: 8,
                }}
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </>
          ) : (
            <Typography variant="body1" sx={{ color: 'white', py: 4 }}>
              Loading camera...
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button
            onClick={closeCamera}
            variant="outlined"
            sx={{ color: 'white', borderColor: 'white' }}
          >
            Cancel
          </Button>
          <Button
            onClick={capturePhoto}
            variant="contained"
            disabled={!stream}
            startIcon={<PhotoCamera />}
          >
            Capture
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OutOfSiteTasks;

